import React, { useState, useRef, useEffect } from 'react';
import { UserPlus, Download, Info, Upload, Check, CheckCircle, X, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import useOrganizationStore from '@/stores/SuperAdminStore/OrganizationStore';
import { uploadUsers } from '@/services/PagesServices/BulkUploadService';
import './AddImportUsersTab.css';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'student' | 'instructor' | 'admin';
  department: string;
  jobTitle: string;
  organizationId: string | string[];
}

interface ValidationError {
  row: number;
  field: string;
  error: string;
}

interface PreviewData {
  [key: string]: string;
}

interface UploadStats {
  totalRows: number;
  valid: number;
  errors: number;
  warnings: number;
  duplicates: number;
}

interface UploadResult {
  totalRows: number;
  inserted: number;
  skipped: number;
  errors: Array<{ row: number; field: string; message: string }>;
  warnings: string[];
  emailsSent?: number;
  emailsFailed?: number;
}

const AddImportUsersTab: React.FC = () => {
  const { departments, creating, createUser } = useUsersStore();
  const { organizations, fetchOrganizations } = useOrganizationStore();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'student',
    department: '',
    jobTitle: '',
    organizationId: '',
  });

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch organizations on mount
  useEffect(() => {
    fetchOrganizations({ isActive: true });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setIsOrgDropdownOpen(false);
      }
    };

    if (isOrgDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOrgDropdownOpen]);

  // CSV upload states
  const [stats, setStats] = useState<UploadStats | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // If role is changed, reset organizationId
    if (name === 'role') {
      setIsOrgDropdownOpen(false); // Close dropdown when role changes
      setFormData((prev) => ({
        ...prev,
        role: value as 'student' | 'instructor' | 'admin',
        organizationId: value === 'admin' ? [] : ''
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOrganizationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Single select for students/instructors
    setFormData((prev) => ({ ...prev, organizationId: e.target.value }));
  };

  const handleOrganizationCheckboxChange = (orgId: string) => {
    setFormData((prev) => {
      const currentIds = Array.isArray(prev.organizationId) ? prev.organizationId : [];
      const newIds = currentIds.includes(orgId)
        ? currentIds.filter(id => id !== orgId)
        : [...currentIds, orgId];
      return { ...prev, organizationId: newIds };
    });
  };

  const toggleOrgDropdown = () => {
    setIsOrgDropdownOpen(!isOrgDropdownOpen);
  };

  const getSelectedOrgNames = () => {
    if (!Array.isArray(formData.organizationId) || formData.organizationId.length === 0) {
      return 'Select organizations';
    }
    const selectedOrgs = organizations.filter(org =>
      formData.organizationId.includes(org.id.toString())
    );
    return selectedOrgs.map(org => org.name).join(', ');
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const result = await createUser(formData);

    if (result.success) {
      toast.success(result.message);

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'student',
        department: '',
        jobTitle: '',
        organizationId: '',
      });
      setIsOrgDropdownOpen(false);

      // Set generated password (will be shown to user)
      if (result.data?.generatedPassword) {
        setGeneratedPassword(result.data.generatedPassword);
      }
    } else {
      toast.error(result.message);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      setError('Invalid file type. Please upload a CSV file.');
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      setError('File is too large. Maximum size is 10MB.');
      return;
    }

    setCsvFile(file);
    setError(null);

    // Parse CSV file for preview
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data as PreviewData[];

        if (data.length === 0) {
          setError('CSV file is empty');
          toast.error('CSV file is empty');
          return;
        }

        // Validate required columns - accept both camelCase and lowercase
        const headers = Object.keys(data[0]).map(h => h.toLowerCase().replace(/\s+/g, ''));
        const requiredFieldsMap = {
          firstname: ['firstname', 'first_name', 'first name'],
          lastname: ['lastname', 'last_name', 'last name'],
          email: ['email', 'e-mail', 'emailaddress'],
          role: ['role', 'user_role', 'userrole']
        };

        const missingFields: string[] = [];
        for (const [field, variants] of Object.entries(requiredFieldsMap)) {
          const found = variants.some(variant => headers.includes(variant.toLowerCase().replace(/\s+/g, '')));
          if (!found) {
            missingFields.push(field);
          }
        }

        if (missingFields.length > 0) {
          setError(`Missing required columns: ${missingFields.join(', ')}`);
          toast.error(`Missing required columns: ${missingFields.join(', ')}`);
          setCsvFile(null);
          return;
        }

        // Set basic stats for preview
        setStats({
          totalRows: data.length,
          valid: data.length,
          errors: 0,
          warnings: 0,
          duplicates: 0
        });

        // Clear previous errors
        setValidationErrors([]);

        // Set preview data (first 5 rows)
        setPreviewData(data.slice(0, 5));
      },
      error: (error) => {
        const errorMsg = `Failed to parse CSV: ${error.message}`;
        setError(errorMsg);
        toast.error(errorMsg);
        setStats(null);
        setValidationErrors([]);
        setPreviewData([]);
      }
    });
  };

  const handleCancelUpload = () => {
    setCsvFile(null);
    setStats(null);
    setValidationErrors([]);
    setPreviewData([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportUsers = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await uploadUsers(csvFile);

      // Store upload results
      setUploadResult({
        totalRows: result.data.totalRows,
        inserted: result.data.inserted || 0,
        skipped: result.data.skipped || 0,
        errors: result.data.errors || [],
        warnings: result.data.warnings || [],
        emailsSent: result.data.emailsSent,
        emailsFailed: result.data.emailsFailed,
      });

      // Show results modal
      setShowResultsModal(true);

      // Reset upload form
      handleCancelUpload();

      const insertedCount = result.data.inserted || 0;
      if (result.success && insertedCount > 0) {
        toast.success(`Upload completed! ${insertedCount} users created successfully.`);
      } else if (insertedCount === 0 && result.data.errors.length > 0) {
        toast.error('No users were uploaded. Please check the error report.');
      }
    } catch (error: any) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = (roleType: 'student' | 'instructor' | 'admin-single' | 'admin-multiple') => {
    const headers = 'first_name,last_name,email,role,department,job_title,organization_name,manager_email,phone,location\n';

    let csvContent = '';
    let fileName = '';

    switch (roleType) {
      case 'student':
        fileName = 'learner_template.csv';
        csvContent = headers +
          'John,Doe,john.doe@company.com,learner,Engineering,Software Engineer,Acme Corporation,jane.smith@company.com,9876543210,New York\n' +
          'Alice,Williams,alice.williams@company.com,learner,Sales,Sales Representative,Tech Solutions,bob.johnson@company.com,7654321098,Boston\n' +
          'David,Miller,david.miller@company.com,learner,Marketing,Marketing Associate,Acme Corporation,emma.davis@company.com,8001234567,Los Angeles';
        break;

      case 'instructor':
        fileName = 'instructor_template.csv';
        csvContent = headers +
          'Jane,Smith,jane.smith@company.com,instructor,Engineering,Senior Developer,Tech Solutions,,9123456789,San Francisco\n' +
          'Bob,Johnson,bob.johnson@company.com,instructor,Marketing,Marketing Manager,Acme Corporation,,8765432109,Chicago\n' +
          'Emma,Davis,emma.davis@company.com,instructor,Design,UX Designer,Global Industries,,7891234560,Austin';
        break;

      case 'admin-single':
        fileName = 'admin_single_org_template.csv';
        csvContent = headers +
          'Grace,Moore,grace.moore@company.com,admin,HR,HR Manager,Acme Corporation,,5559876543,Seattle\n' +
          'Frank,Wilson,frank.wilson@company.com,admin,Operations,Operations Lead,Tech Solutions,,6667778888,Denver\n' +
          'Isabel,Anderson,isabel.anderson@company.com,admin,IT,IT Director,Global Industries,,5553334444,Miami';
        break;

      case 'admin-multiple':
        fileName = 'admin_multiple_orgs_template.csv';
        csvContent = headers +
          'Charlie,Brown,charlie.brown@company.com,admin,IT,System Administrator,"Acme Corporation, Tech Solutions",,5551234567,Seattle\n' +
          'Henry,Taylor,henry.taylor@company.com,admin,Management,Director,"Acme Corporation, Tech Solutions, Global Industries",,5559998888,Portland\n' +
          'Kate,Lee,kate.lee@company.com,admin,Executive,VP of Operations,"Tech Solutions, Global Industries",,5557776666,Phoenix';
        break;
    }

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`${fileName} downloaded successfully`);
  };

  const handleDownloadReport = () => {
    if (!uploadResult) return;

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fileName = `upload_report_${timestamp}.csv`;

    // Create report content
    let reportContent = '=== BULK UPLOAD REPORT ===\n\n';
    reportContent += `Upload Date: ${new Date().toLocaleString()}\n`;
    reportContent += `Total Rows Processed: ${uploadResult.totalRows}\n`;
    reportContent += `Successfully Uploaded: ${uploadResult.inserted}\n`;
    reportContent += `Failed/Skipped: ${uploadResult.skipped}\n`;
    if (uploadResult.emailsSent !== undefined) {
      reportContent += `Invitation Emails Sent: ${uploadResult.emailsSent}\n`;
    }
    if (uploadResult.emailsFailed !== undefined && uploadResult.emailsFailed > 0) {
      reportContent += `Invitation Emails Failed: ${uploadResult.emailsFailed}\n`;
    }
    reportContent += '\n\n';

    // Add errors section
    if (uploadResult.errors.length > 0) {
      reportContent += '=== FAILED ROWS (Errors) ===\n';
      reportContent += 'Row Number,Field,Error Reason\n';
      uploadResult.errors.forEach((error) => {
        reportContent += `${error.row},"${error.field}","${error.message}"\n`;
      });
      reportContent += '\n\n';
    }

    // Add warnings section
    if (uploadResult.warnings.length > 0) {
      reportContent += '=== WARNINGS ===\n';
      uploadResult.warnings.forEach((warning, index) => {
        reportContent += `${index + 1}. ${warning}\n`;
      });
      reportContent += '\n\n';
    }

    // Add success summary
    if (uploadResult.inserted > 0) {
      reportContent += '=== SUCCESS SUMMARY ===\n';
      reportContent += `${uploadResult.inserted} user(s) were successfully created and invitation emails were sent.\n`;
      reportContent += 'These users can now log in with their temporary passwords.\n';
    }

    // Create and download the file
    const blob = new Blob([reportContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Upload report downloaded successfully');
  };

  return (
    <div className="admin-user-import-container">
      <div className="admin-user-import-card">
        <div className="admin-user-import-card-header">
          <div className="admin-user-import-card-icon orange">
            <UserPlus size={28} />
          </div>
          <div className="admin-user-import-card-title">
            <h2>Add Single User</h2>
            <p>Create a new user account manually</p>
          </div>
        </div>

        <form onSubmit={handleCreateUser}>
          <div className="admin-user-import-form-row">
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">First Name *</label>
              <input
                type="text"
                name="firstName"
                className="admin-user-import-form-input"
                placeholder="John"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Last Name *</label>
              <input
                type="text"
                name="lastName"
                className="admin-user-import-form-input"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="admin-user-import-form-group">
            <label className="admin-user-import-form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="admin-user-import-form-input"
              placeholder="john.doe@company.com"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="admin-user-import-form-row">
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Role</label>
              <select
                name="role"
                className="admin-user-import-form-input"
                value={formData.role}
                onChange={handleInputChange}
              >
                <option value="student">Learner</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Department</label>
              <select
                name="department"
                className="admin-user-import-form-input"
                value={formData.department}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
                <option value="Engineering">Engineering</option>
                <option value="Sales">Sales</option>
                <option value="HR">HR</option>
                <option value="Design">Design</option>
              </select>
            </div>
          </div>

          <div className="admin-user-import-form-row">
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Job Title</label>
              <input
                type="text"
                name="jobTitle"
                className="admin-user-import-form-input"
                placeholder="Software Engineer"
                value={formData.jobTitle}
                onChange={handleInputChange}
              />
            </div>
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">
                Organization (Optional)
              </label>
              {formData.role === 'admin' ? (
                <div ref={orgDropdownRef} style={{ position: 'relative' }}>
                  <div
                    onClick={toggleOrgDropdown}
                    className="admin-user-import-form-input"
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      minHeight: '42px',
                      padding: '8px 12px'
                    }}
                  >
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: Array.isArray(formData.organizationId) && formData.organizationId.length > 0 ? '#1f2937' : '#9ca3af'
                    }}>
                      {getSelectedOrgNames()}
                    </span>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      style={{
                        transform: isOrgDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                    >
                      <path d="M5 7.5L10 12.5L15 7.5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {isOrgDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        marginTop: '4px',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        zIndex: 1000
                      }}
                    >
                      {organizations.filter(org => org.isActive).length === 0 ? (
                        <div style={{ padding: '12px', color: '#9ca3af', textAlign: 'center' }}>
                          No organizations available
                        </div>
                      ) : (
                        organizations.filter(org => org.isActive).map((org) => (
                          <label
                            key={org.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '10px 12px',
                              cursor: 'pointer',
                              transition: 'background-color 0.15s',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={Array.isArray(formData.organizationId) && formData.organizationId.includes(org.id.toString())}
                              onChange={() => handleOrganizationCheckboxChange(org.id.toString())}
                              style={{
                                width: '16px',
                                height: '16px',
                                marginRight: '10px',
                                cursor: 'pointer',
                                accentColor: '#3b82f6'
                              }}
                            />
                            <span style={{ fontSize: '14px', color: '#1f2937' }}>
                              {org.name}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <select
                  name="organizationId"
                  className="admin-user-import-form-input"
                  value={formData.organizationId as string}
                  onChange={handleOrganizationChange}
                >
                  <option value="">None</option>
                  {organizations.filter(org => org.isActive).map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <button type="submit" className="admin-user-import-submit-btn" disabled={creating}>
            <UserPlus size={18} />
            {creating ? 'Creating...' : 'Create User'}
          </button>
        </form>

        {generatedPassword && (
          <div className="admin-user-import-password-display">
            <Check size={18} />
            <span>User created! Temporary password: <strong>{generatedPassword}</strong></span>
            <button onClick={() => {
              navigator.clipboard.writeText(generatedPassword);
              toast.success('Password copied to clipboard');
            }}>
              Copy
            </button>
          </div>
        )}
      </div>

      <div className="admin-user-import-card">
        <div className="admin-user-import-card-header">
          <div className="admin-user-import-card-icon blue">
            <Upload size={28} />
          </div>
          <div className="admin-user-import-card-title">
            <h2>Bulk Import</h2>
            <p>Import multiple users from CSV file</p>
          </div>
        </div>

        <div className="admin-user-import-info-box">
          <div className="admin-user-import-info-title">
            <Info size={18} />
            CSV Format Requirements
          </div>
          <ul className="admin-user-import-info-list">
            <li><strong>Required Columns:</strong> firstName (or firstname), lastName (or lastname), email, role</li>
            <li><strong>Optional Columns:</strong> department, jobTitle, organization_name</li>
            <li><strong>Organizations:</strong> Learners/Instructors can have 1 organization. Admins can have multiple (comma-separated)</li>
            <li><strong>Valid Role Values:</strong> learner, instructor, admin</li>
            <li><strong>Limits:</strong> Max file size 10MB | Max 1000 rows per upload</li>
          </ul>
          <button
            onClick={() => setShowSampleModal(true)}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
          >
            <Info size={16} />
            View Sample CSV Data
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <button
            className="admin-user-import-download-btn"
            onClick={() => handleDownloadTemplate('student')}
            style={{
              backgroundColor: '#059669',
              borderColor: '#059669'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
          >
            <Download size={18} />
            Learner Template
          </button>

          <button
            className="admin-user-import-download-btn"
            onClick={() => handleDownloadTemplate('instructor')}
            style={{
              backgroundColor: '#0891b2',
              borderColor: '#0891b2'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0e7490'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0891b2'}
          >
            <Download size={18} />
            Instructor Template
          </button>

          <button
            className="admin-user-import-download-btn"
            onClick={() => handleDownloadTemplate('admin-single')}
            style={{
              backgroundColor: '#dc2626',
              borderColor: '#dc2626'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
          >
            <Download size={18} />
            Admin (Single Org)
          </button>

          <button
            className="admin-user-import-download-btn"
            onClick={() => handleDownloadTemplate('admin-multiple')}
            style={{
              backgroundColor: '#7c3aed',
              borderColor: '#7c3aed'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d28d9'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
          >
            <Download size={18} />
            Admin (Multiple Orgs)
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            color: '#c33',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={18} />
            <strong>Error:</strong> {error}
          </div>
        )}

        {!csvFile ? (
          <div
            className="admin-user-import-upload-area"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <div className="admin-user-import-upload-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <path
                  d="M28 14V35M28 14L21 21M28 14L35 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 35V42C14 43.1046 14.8954 44 16 44H40C41.1046 44 42 43.1046 42 42V35"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="admin-user-import-upload-text">
              Drop CSV file here or click to browse
            </div>
            <div className="admin-user-import-upload-subtext">Supports .csv files up to 10MB</div>
          </div>
        ) : (
          <>
            {/* File Info */}
            <div style={{
              backgroundColor: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Check size={24} style={{ color: '#16a34a' }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#166534' }}>{csvFile.name}</div>
                  <div style={{ fontSize: '13px', color: '#16a34a' }}>
                    {(csvFile.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </div>
              <button
                onClick={handleCancelUpload}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#dc2626',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#111827' }}>
                    {stats.totalRows}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Total Rows</div>
                </div>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #86efac'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: '#16a34a' }}>
                    {stats.valid}
                  </div>
                  <div style={{ fontSize: '13px', color: '#16a34a' }}>Valid</div>
                </div>
                {stats.errors > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: '#dc2626' }}>
                      {stats.errors}
                    </div>
                    <div style={{ fontSize: '13px', color: '#dc2626' }}>Errors</div>
                  </div>
                )}
                {stats.warnings > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fde047'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: '#ca8a04' }}>
                      {stats.warnings}
                    </div>
                    <div style={{ fontSize: '13px', color: '#ca8a04' }}>Warnings</div>
                  </div>
                )}
              </div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <div style={{
                  fontWeight: 600,
                  color: '#dc2626',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={18} />
                  Validation Errors ({validationErrors.length})
                </div>
                <div style={{ fontSize: '13px' }}>
                  {validationErrors.slice(0, 10).map((err, index) => (
                    <div key={index} style={{ padding: '4px 0', color: '#991b1b' }}>
                      Row {err.row}: <strong>{err.field}</strong> - {err.error}
                    </div>
                  ))}
                  {validationErrors.length > 10 && (
                    <div style={{ padding: '4px 0', color: '#991b1b', fontStyle: 'italic' }}>
                      ... and {validationErrors.length - 10} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Data Preview */}
            {previewData.length > 0 && (
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '16px'
              }}>
                <div style={{ fontWeight: 600, color: '#111827', marginBottom: '12px' }}>
                  Data Preview (First 5 rows)
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>First Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Last Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Email</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Role</th>
                        <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Organization</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, index) => {
                        // Helper function to find field value regardless of naming convention
                        const getFieldValue = (fieldVariants: string[]) => {
                          for (const variant of fieldVariants) {
                            const value = row[variant];
                            if (value) return value;
                          }
                          return '-';
                        };

                        // Get organization name
                        const organizationDisplay = getFieldValue(['organization_name', 'organizationName', 'OrganizationName', 'Organization Name']);

                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px' }}>
                              {getFieldValue(['firstName', 'firstname', 'FirstName', 'first_name', 'First Name'])}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {getFieldValue(['lastName', 'lastname', 'LastName', 'last_name', 'Last Name'])}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {getFieldValue(['email', 'Email', 'e-mail', 'E-mail'])}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {getFieldValue(['role', 'Role', 'user_role', 'User Role']) || 'learner'}
                            </td>
                            <td style={{ padding: '8px', color: organizationDisplay === '-' ? '#9ca3af' : 'inherit' }}>
                              {organizationDisplay}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleCancelUpload}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  cursor: 'pointer'
                }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="admin-user-import-submit-btn"
                onClick={handleImportUsers}
                disabled={uploading || validationErrors.length > 0}
                style={{ flex: 1 }}
              >
                <CheckCircle size={18} />
                {uploading ? 'Uploading...' : 'Confirm & Upload'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Sample CSV Data Modal */}
      {showSampleModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowSampleModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                CSV Sample Data & Format Guide
              </h3>
              <button
                onClick={() => setShowSampleModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Important Notes */}
              <div style={{
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>
                  📌 Important Notes
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#1e40af', lineHeight: '1.6' }}>
                  <li><strong>Required fields:</strong> first_name, last_name, email, role</li>
                  <li><strong>Optional fields:</strong> Leave empty or skip the column entirely</li>
                  <li><strong>Multiple organizations (Admin only):</strong> Use comma-separated values in quotes</li>
                  <li><strong>Single organization (Student/Instructor):</strong> Just enter the name, no quotes needed</li>
                </ul>
              </div>

              {/* Student Example */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#059669', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} />
                  Example 1: Learner with Organization
                </h4>
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    John,Doe,john.doe@company.com,learner,Engineering,Software Engineer,Acme Corporation,jane@company.com,9876543210,New York
                  </code>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  ✓ All fields filled | Organization: Single value
                </p>
              </div>

              {/* Student Example - Skipping Optional Fields */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#059669', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} />
                  Example 2: Learner - Skipping Optional Fields
                </h4>
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    Alice,Williams,alice@company.com,learner,,,,,,
                  </code>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  ✓ Only required fields filled | Optional fields left empty (commas preserved)
                </p>
              </div>

              {/* Instructor Example */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#0891b2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} />
                  Example 3: Instructor with Partial Data
                </h4>
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    Bob,Johnson,bob@company.com,instructor,Marketing,Manager,Tech Solutions,,,
                  </code>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  ✓ Required + some optional fields | Phone and location skipped
                </p>
              </div>

              {/* Admin Example - Single Organization */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} />
                  Example 4: Admin with Single Organization
                </h4>
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    Jane,Smith,jane@company.com,admin,Engineering,Senior Dev,Acme Corporation,,9123456789,
                  </code>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  ✓ Admin with one organization | Manager email skipped, location skipped
                </p>
              </div>

              {/* Admin Example - Multiple Organizations */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={18} />
                  Example 5: Admin with Multiple Organizations
                </h4>
                <div style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    Charlie,Brown,charlie@company.com,admin,IT,System Admin,"Acme Corporation, Tech Solutions, Global Industries",,5551234567,Seattle
                  </code>
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  ⚠️ <strong>Important:</strong> Multiple organizations MUST be wrapped in quotes | Comma-separated
                </p>
              </div>

              {/* CSV Header */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: '#1f2937' }}>
                  Complete CSV Header Row
                </h4>
                <div style={{
                  backgroundColor: '#1f2937',
                  color: '#10b981',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  overflowX: 'auto'
                }}>
                  <code>
                    first_name,last_name,email,role,department,job_title,organization_name,manager_email,phone,location
                  </code>
                </div>
              </div>

              {/* Field Descriptions */}
              <div style={{
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
                  Field Descriptions
                </h4>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Field</th>
                      <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Required</th>
                      <th style={{ padding: '8px', textAlign: 'left', color: '#6b7280' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>first_name</td>
                      <td style={{ padding: '8px', color: '#dc2626' }}>Yes</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>User's first name</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>last_name</td>
                      <td style={{ padding: '8px', color: '#dc2626' }}>Yes</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>User's last name</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>email</td>
                      <td style={{ padding: '8px', color: '#dc2626' }}>Yes</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>Must be valid email format</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>role</td>
                      <td style={{ padding: '8px', color: '#dc2626' }}>Yes</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>learner, instructor, or admin</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>department</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>User's department</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>job_title</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>Job title or designation</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>organization_name</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>Single for learner/instructor, multiple (quoted) for admin</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>manager_email</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>Manager's email address</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 500 }}>phone</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>Contact phone number</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 500 }}>location</td>
                      <td style={{ padding: '8px', color: '#059669' }}>No</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>City or office location</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowSampleModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Results Modal */}
      {showResultsModal && uploadResult && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowResultsModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: uploadResult.inserted > 0 ? '#f0fdf4' : '#fef2f2'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                  Upload Complete
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                  {uploadResult.totalRows} rows processed
                </p>
              </div>
              <button
                onClick={() => setShowResultsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '8px',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <CheckCircle size={20} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#166534' }}>Successfully Uploaded</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#16a34a' }}>
                    {uploadResult.inserted}
                  </div>
                  <div style={{ fontSize: '13px', color: '#16a34a', marginTop: '4px' }}>
                    {uploadResult.inserted === 1 ? 'user created' : 'users created'}
                  </div>
                </div>

                <div style={{
                  padding: '16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '2px solid #fecaca'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <AlertCircle size={20} style={{ color: '#dc2626' }} />
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b' }}>Failed/Skipped</span>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 700, color: '#dc2626' }}>
                    {uploadResult.skipped}
                  </div>
                  <div style={{ fontSize: '13px', color: '#dc2626', marginTop: '4px' }}>
                    {uploadResult.skipped === 1 ? 'row had errors' : 'rows had errors'}
                  </div>
                </div>
              </div>

              {/* Success Section */}
              {uploadResult.inserted > 0 && (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <CheckCircle size={18} style={{ color: '#16a34a' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#166534' }}>
                      Successfully Created Users
                    </h4>
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#16a34a', lineHeight: '1.6' }}>
                    ✓ {uploadResult.inserted} {uploadResult.inserted === 1 ? 'user was' : 'users were'} successfully created<br />
                    ✓ Temporary passwords generated<br />
                    {uploadResult.emailsSent !== undefined && (
                      <>✓ {uploadResult.emailsSent} invitation {uploadResult.emailsSent === 1 ? 'email' : 'emails'} sent<br /></>
                    )}
                    ✓ Users can now log in to the system
                  </p>
                </div>
              )}

              {/* Error Section */}
              {uploadResult.errors.length > 0 && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <AlertCircle size={18} style={{ color: '#dc2626' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#991b1b' }}>
                      Rejected Users ({uploadResult.errors.length})
                    </h4>
                  </div>
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    fontSize: '13px'
                  }}>
                    {uploadResult.errors.map((error, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          marginBottom: '8px',
                          border: '1px solid #fee2e2'
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px' }}>
                          Row {error.row} - {error.field}
                        </div>
                        <div style={{ color: '#991b1b' }}>
                          {error.message}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings Section */}
              {uploadResult.warnings.length > 0 && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde047',
                  borderRadius: '8px',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <AlertCircle size={18} style={{ color: '#ca8a04' }} />
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#a16207' }}>
                      Warnings ({uploadResult.warnings.length})
                    </h4>
                  </div>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    fontSize: '13px'
                  }}>
                    {uploadResult.warnings.map((warning, index) => (
                      <div key={index} style={{ padding: '4px 0', color: '#a16207' }}>
                        • {warning}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleDownloadReport}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#15803d'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#16a34a'}
              >
                <Download size={16} />
                Download Report
              </button>
              <button
                onClick={() => setShowResultsModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddImportUsersTab;
